import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import {
  convertUnitToTonnes,
  UnitOfWeight,
} from '@forms/client/AddProductForm/utils/format';
import { SUPPORTED_CURRENCY_GROUPS } from '@constants';
import { Price } from '@views/Noticeboard/types';
import { buildInventoryImageUrl } from '../../../utils/inventoryImage';
import {
  skuPackDescription,
  skuProductsPerUnit,
  skuUnitNoun,
} from '../../../utils/sku';
import { lostStockOf, surplusStockOf } from '../../../utils/stockDisplay';
import { skuBrand } from './skuFacets';

type SkuDimensions = InventorySKUListing['dimensions'];
type SkuPallet = InventorySKUListing['palletising'][number];
export type MeasuredDimensions = {
  length: number | null;
  width: number | null;
  height: number | null;
  dimensionsUnitOfMeasure: string | null;
};

export const skuCode = (sku: InventorySKUListing): string =>
  sku.skuId || sku.inventorySkuId;

export const skuThumbnailSrc = (sku: InventorySKUListing): string | null =>
  buildInventoryImageUrl(
    sku.images?.[0] ?? sku.productImages?.[0] ?? sku.productDetails?.image
  );

export const skuOwnThumbnailSrc = (sku: InventorySKUListing): string | null =>
  buildInventoryImageUrl(sku.images?.[0]);

export const productThumbnailSrcOfSku = (
  sku: InventorySKUListing
): string | null =>
  buildInventoryImageUrl(sku.productDetails?.image ?? sku.productImages?.[0]);

export const formatMeasuredDimensions = (
  dimensions: MeasuredDimensions
): string =>
  dimensions.length != null &&
  dimensions.width != null &&
  dimensions.height != null
    ? `${dimensions.length} × ${dimensions.width} × ${dimensions.height} ${
        dimensions.dimensionsUnitOfMeasure ?? ''
      }`.trim()
    : '—';

export const formatSkuDimensions = (dimensions: SkuDimensions): string =>
  formatMeasuredDimensions(dimensions);

export const formatPalletDimensions = (pallet: SkuPallet): string =>
  formatMeasuredDimensions(pallet.dimensions);

export const formatWeight = (
  weight: number | null | undefined,
  unitOfMeasure: string | null | undefined
): string =>
  weight != null
    ? `${numberToLocale(weight)} ${unitOfMeasure ?? ''}`.trim()
    : '—';

export const formatSkuWeight = (sku: InventorySKUListing): string =>
  formatWeight(sku.grossWeight, sku.grossWeightUnitOfMeasure);

export const skuSubtext = (sku: InventorySKUListing): string => {
  const productsPerUnit = skuProductsPerUnit(sku);
  const brand = skuBrand(sku);
  return [
    sku.unitOfMeasure ? `Base UoM: ${sku.unitOfMeasure}` : null,
    productsPerUnit > 1 ? `${numberToLocale(productsPerUnit)} products` : null,
    brand ? `Brand: ${brand}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

export const palletSkusPerPallet = (pallet: SkuPallet): number | null =>
  pallet.amountOfSkusInLayer != null && pallet.amountOfLayersInPallet != null
    ? pallet.amountOfSkusInLayer * pallet.amountOfLayersInPallet
    : null;

const cubicMetreDivisors: Record<string, number> = {
  mm: 1_000_000_000,
  cm: 1_000_000,
  m: 1,
};

export const palletVolume = (pallet: SkuPallet): string => {
  const { length, width, height, dimensionsUnitOfMeasure } = pallet.dimensions;
  const divisor = cubicMetreDivisors[dimensionsUnitOfMeasure?.toLowerCase()];
  if (length == null || width == null || height == null || !divisor) return '—';
  return `${((length * width * height) / divisor).toFixed(2)} m³`;
};

export const skusPerPalletWithUnit = (
  sku: InventorySKUListing,
  pallet: SkuPallet
): string => {
  const perPallet = palletSkusPerPallet(pallet);
  if (perPallet == null) return '—';
  const unit = skuUnitNoun(sku).toLowerCase();
  return `${numberToLocale(perPallet)} ${unit}${perPallet === 1 ? '' : 's'}`;
};

const currencySymbolOf = (currencyCode: string | undefined): string =>
  SUPPORTED_CURRENCY_GROUPS.flatMap((group) => group.currencies).find(
    (currency) => currency.code === currencyCode
  )?.symbol ?? `${currencyCode ?? ''} `;

export const formatSkuPrice = (price: Price): string =>
  price.amount != null
    ? `${currencySymbolOf(price.currency)}${Number(price.amount).toFixed(2)}`
    : '—';

export const palletStackedCube = (
  sku: InventorySKUListing,
  pallet: SkuPallet
): string => {
  const unitsPerPallet = palletSkusPerPallet(pallet);
  const { length, width, height, dimensionsUnitOfMeasure } = sku.dimensions;
  if (
    unitsPerPallet == null ||
    length == null ||
    width == null ||
    height == null
  )
    return '—';
  return `${numberToLocale(
    Number((length * width * height * unitsPerPallet).toFixed(2))
  )} ${dimensionsUnitOfMeasure ?? ''}³`.trim();
};

export const palletStackedWeight = (
  sku: InventorySKUListing,
  pallet: SkuPallet
): string => {
  const unitsPerPallet = palletSkusPerPallet(pallet);
  if (unitsPerPallet == null || !sku.grossWeight) return '—';
  const tonnes = convertUnitToTonnes(
    sku.grossWeight * unitsPerPallet,
    sku.grossWeightUnitOfMeasure as UnitOfWeight
  );
  if (!Number.isFinite(tonnes)) return '—';
  const roundedTonnes = Number(tonnes.toFixed(2));
  return `${roundedTonnes} ${roundedTonnes === 1 ? 'tonne' : 'tonnes'}`;
};

export const formatPalletWeight = (pallet: SkuPallet): string =>
  pallet.palletWeight != null
    ? `${numberToLocale(pallet.palletWeight)} ${
        pallet.palletWeightUnitOfMeasure ?? ''
      }`.trim()
    : '—';

export const skuPackagingSummaries = (sku: InventorySKUListing): string[] =>
  (sku.palletising ?? []).map((pallet) => {
    const perPallet = palletSkusPerPallet(pallet);
    const spec = pallet.customPalletSpecification
      ? ` (${pallet.customPalletSpecification})`
      : '';
    const perPalletNote =
      perPallet != null
        ? ` = ${numberToLocale(perPallet)} ${skuUnitNoun(
            sku
          ).toLowerCase()}s/pallet`
        : '';
    return `${pallet.type}${spec}: ${
      pallet.amountOfSkusInLayer ?? '—'
    }/layer × ${pallet.amountOfLayersInPallet ?? '—'} layers${perPalletNote}`;
  });

export const dedupeSkusById = (
  skus: InventorySKUListing[]
): InventorySKUListing[] => {
  const seenIds = new Set<string>();
  return skus.filter((sku) => {
    if (seenIds.has(sku.inventorySkuId)) return false;
    seenIds.add(sku.inventorySkuId);
    return true;
  });
};

export const stockWithProducts = (
  sku: InventorySKUListing,
  stockUnits: number
): string => {
  const productsPerUnit = skuProductsPerUnit(sku);
  if (productsPerUnit <= 1) return numberToLocale(stockUnits);
  const unit = skuUnitNoun(sku).toLowerCase();
  return `${numberToLocale(
    stockUnits * productsPerUnit
  )} products (${numberToLocale(stockUnits)} ${unit}${
    stockUnits === 1 ? '' : 's'
  })`;
};

export const skuBarcode = (sku: InventorySKUListing): string | null =>
  sku.barcode?.barcode ?? sku.productDetails?.barcode?.barcode ?? null;

export const skuDescription = (sku: InventorySKUListing): string | null =>
  sku.description || sku.productDetails?.description || null;

export type PackagingStep = {
  key: string;
  title: string;
  detail: string | null;
  imageSrc: string | null;
  quantityLabel: string;
  barcode: string | null;
  isBaseUnit: boolean;
  palletIndex: number | null;
};

const productNameOf = (sku: InventorySKUListing): string | null =>
  sku.productDetails?.name ?? sku.productName ?? null;

const productBarcodeOf = (sku: InventorySKUListing): string | null =>
  sku.productDetails?.barcode?.barcode ?? null;

const detailUnlessRepeated = (
  detail: string | null,
  title: string
): string | null => (detail === title ? null : detail);

const palletQuantityLabel = (
  sku: InventorySKUListing,
  pallet: SkuPallet
): string => {
  const unitsPerPallet = palletSkusPerPallet(pallet);
  if (unitsPerPallet == null) return 'Qty —';
  const productsPerUnit = skuProductsPerUnit(sku);
  const unitsText = skusPerPalletWithUnit(sku, pallet);
  if (productsPerUnit <= 1) return unitsText;
  return `${unitsText} · ${numberToLocale(
    unitsPerPallet * productsPerUnit
  )} products`;
};

export const packagingStepsOf = (sku: InventorySKUListing): PackagingStep[] => {
  const productsPerUnit = skuProductsPerUnit(sku);
  const isPackedInMultiples = productsPerUnit > 1;

  const itemStep: PackagingStep = {
    key: 'item',
    title: 'Individual item',
    detail: detailUnlessRepeated(
      isPackedInMultiples ? productNameOf(sku) : sku.name,
      'Individual item'
    ),
    imageSrc: isPackedInMultiples
      ? productThumbnailSrcOfSku(sku)
      : skuThumbnailSrc(sku),
    quantityLabel: 'Qty 1',
    barcode: isPackedInMultiples ? productBarcodeOf(sku) : skuBarcode(sku),
    isBaseUnit: true,
    palletIndex: null,
  };

  const packDescription = skuPackDescription(sku);
  const packStep: PackagingStep = {
    key: 'pack',
    title: packDescription,
    detail: detailUnlessRepeated(sku.name, packDescription),
    imageSrc: skuOwnThumbnailSrc(sku),
    quantityLabel: `Qty ${numberToLocale(productsPerUnit)}`,
    barcode: sku.barcode?.barcode ?? null,
    isBaseUnit: false,
    palletIndex: null,
  };

  const palletSteps = (sku.palletising ?? []).map((pallet, index) => ({
    key: `pallet-${index}`,
    title: pallet.type,
    detail: detailUnlessRepeated(pallet.customPalletSpecification, pallet.type),
    imageSrc: null,
    quantityLabel: palletQuantityLabel(sku, pallet),
    barcode: null,
    isBaseUnit: false,
    palletIndex: index,
  }));

  return [itemStep, ...(isPackedInMultiples ? [packStep] : []), ...palletSteps];
};

export const skuInformationItems = (
  sku: InventorySKUListing
): { label: string; value: string }[] => {
  const infoItems = [
    {
      label: 'Unit of Measure',
      value: sku.unitOfMeasure || '-',
    },
    {
      label: 'Gross Weight',
      value:
        sku.grossWeight != null
          ? `${numberToLocale(sku.grossWeight)}${
              sku.grossWeightUnitOfMeasure ?? ''
            }`
          : '-',
    },
    {
      label: 'Dimensions',
      value: formatSkuDimensions(sku.dimensions),
    },
    {
      label: `Products per ${skuUnitNoun(sku)}`,
      value:
        (
          sku.amountOfProductInSku ?? sku.productDetails?.amountOfProductInSku
        )?.toString() ?? '-',
    },
  ];

  if (sku.temperatureControlRequirements?.temperatureControlType) {
    infoItems.push({
      label: 'Required Temperature Control',
      value: `${sku?.temperatureControlRequirements?.temperatureControlType}`,
    });
  }

  if (sku.temperatureControlRequirements?.minimumTemperature) {
    infoItems.push({
      label: 'Minimum Temperature',
      value: `${sku.temperatureControlRequirements.minimumTemperature.quantity}${sku.temperatureControlRequirements.minimumTemperature.unitOfMeasure}`,
    });
  }

  if (sku.temperatureControlRequirements?.maximumTemperature) {
    infoItems.push({
      label: 'Maximum Temperature',
      value: `${sku.temperatureControlRequirements.maximumTemperature.quantity}${sku.temperatureControlRequirements.maximumTemperature.unitOfMeasure}`,
    });
  }

  return infoItems;
};

export const hasStockEntries = (sku: InventorySKUListing): boolean =>
  sku.stockByWarehouse?.some(
    (s) =>
      s.totalStock > 0 ||
      s.blockedStock > 0 ||
      s.stockDiscrepancy > 0 ||
      lostStockOf(s) > 0 ||
      surplusStockOf(s) > 0 ||
      s.inOutboundTransitStock > 0 ||
      s.inInboundTransitStock > 0 ||
      s.allocatedStock > 0 ||
      s.availableStock > 0
  ) ?? false;
